import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { zip } from 'underscore';
import {
  formatDate,
  formatPercent,
  formatNumber,
} from '../functions';
import i18n from '../../../i18n';
import AnalysisCard from 'wt-react/dist/components/AnalysisCard/AnalysisCard';
// import { test } from 'wwt-react/dist/util/functions';
import './Model.css';

class Model extends PureComponent {

  KEY_METRICS = [
    'performance',
    'income',
    'risk',
    'sharpe',
    'fees',
  ]

  // Get a dictionary of the key metrics formatted correctly
  getKeyMetrics(statistics) {
    return ({
      performance: formatPercent(statistics.avgAnnualPerformance, 1, false),
      income: formatPercent(statistics.dividendYield, 1, false),
      risk: formatPercent(statistics.avgAnnualVolatility, 1, false),
      sharpe: formatNumber(statistics.sharpeRatio, 2),
      fees: formatNumber(statistics.aggregateExpenseRatio * 10000, 0),
    })
  }

  getBoldedKeyMetrics(values, values2) {
    return ({
      performance: Math.max(values.performance, values2.performance).toString(),
      income: Math.max(values.income, values2.income).toString(),
      risk: Math.min(values.risk, values2.risk).toString(),
      sharpe: Math.max(values.sharpe, values2.sharpe).toString(),
      fees: Math.min(values.fees, values2.fees).toString(),
    })
  }

  // Get a dictionary of the key metrics' symbols 
  getKeyMetricsSymbols() {
    return ({
      performance: '%',
      income: '%',
      risk: '%',
      sharpe: '',
      fees: 'bps',
    })
  }

  // Get a dictionary of which metrics are clickable (have their own charts)
  getKeyMetricsClicks() {
    return ({
      performance: true,
      income: true,
      risk: false,
      sharpe: false,
      fees: false,
    })
  }

  renderKeyMetrics() {
    const {
      strategy,
      model,
      name,
    } = this.props;

    if (model.statistics) {
      const metricsValues = this.getKeyMetrics(strategy.statistics);
      const metricsValues2 = this.getKeyMetrics(model.statistics);
      const metricsBold = this.getBoldedKeyMetrics(metricsValues, metricsValues2);

      const metricsSymbols = this.getKeyMetricsSymbols();
      const metricClicks = this.getKeyMetricsClicks();
      // Build metric data and labels
      const metricData = this.KEY_METRICS.map(metric => {
        return {
          name: metric + 'model',
          title: i18n.t(metric),
          tooltip: i18n.t(metric + '-tooltip')
            .replace("[[FROM]]", formatDate(model.commonPeriodStart))
            .replace("[[TO]]", formatDate(model.commonPeriodEnd))
            .replace("[[ASOF]]", formatDate(model.commonPeriodEnd)),
          description: i18n.t(metric + '-desc'),
          value: metricsValues[metric],
          value2: metricsValues2[metric],
          boldValue: metricsBold[metric],
          symbol: metricsSymbols[metric],
          clickable: metricClicks[metric],
        }
      })
      return (
        <React.Fragment>
          <AnalysisCard
            idOfCard='model-key-metrics'
            topOfAppId=''
            key={'keymetrics-card' + model.id}
            chartType='keymetrics'
            cardTitle='Key Metrics Comparison'
            seriesData={metricData}
            //footnoteText={'* As of ' + formatDate(model.commonPeriodEnd) + ' with a Common Period from ' + formatDate(model.commonPeriodStart) + ' to ' + formatDate(model.commonPeriodEnd) + '.'}
            //showDetailsOnHover={true}
            metricComparisonNames={[name, 'Proposed Model']}
          />
        </React.Fragment>
      )
    } else {
      return null;
    }
  }

  renderHoldingsChart() {
    const {
      model,
    } = this.props;

    if (!model) {
      return null;
    }

    // Get the latest holdings
    const holdings = model.holdings[model.holdings.length - 1];

    // Function to filter unique entries in array
    function onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    }

    // Get distinct Asset Classes
    const assetClasses = holdings.assetClasses.filter(onlyUnique);
    // Zip together the relevant data
    const zippedData = zip(holdings.assetClasses, holdings.tickers, holdings.weights);

    let data = [];
    // Loop over each asset class and put the data series together
    assetClasses.forEach(function (assetClass) {
      // Find the elements in the given asset class
      let assetClassZip = zippedData.filter(zd => zd[0] === assetClass);
      
      // Total up the weights
      let sumWeights = assetClassZip.map((x)=>x[2]).reduce(function sumofArray(sum, num) {
        return sum + num;
      });
      // Build each treenode by asset class with holdings at the child level
      let assetClassData = {
        name: assetClass,
        value: formatPercent(sumWeights, 1, false),
        id: assetClass,
        children: assetClassZip.map(holding => {
          return {
            name: holding[1],
            value: formatPercent(holding[2], 1, false),
            id: assetClass,
            label: {
              show: true,
              formatter: '{b}',
            },
            link: '#fundchar&tickers=' + holding[1],
            target: '_self',
          }
        })
      }
      // Add that asset class to the array
      data.push(assetClassData);
    });

    return (
      <div>
        <AnalysisCard
          idOfCard='model-holdings'
          topOfAppId=''
          key='holdings'
          chartType='treemap'
          cardTitle='Composition'
          showChartLabels={false}
          showLegend={false}
          categoryIsDate={true}
          seriesData={data}
          showGrid={false}
          showAxes={false}
          heroNumber={holdings.tickers.length}
          heroNumberIsPercent={false}
          heroTitle={i18n.t('number-of-holdings')}
          showDetailsOnHover={false}
        />
        <ReactTooltip id={'click-to-edit-holdings-tooltip' + model.id} type='light' className='tooltip' effect="float">
          {i18n.t('click-to-edit-holdings-tooltip')}
        </ReactTooltip>
      </div>
    )
  }

  renderInfoText() {
    const {
      model,
    } = this.props;

    let description = '<span style="font-size:13px;line-height:1.15em;">';
    if(model.textPhilosophy) {
      description += model.textPhilosophy[0].collText + '<br /><br />';
    }
    if(model.textRiskTolerance) {
      description += model.textRiskTolerance[0].collText + '<br /><br />';
    }
    if(model.portURL) {
      description += '<a href="' + model.portURL  + '" style="color:#0fcbd4" target="_new"/>Click for More Info</a>';
    }
    description += '</span>'

    if (model) {
      return (
        <React.Fragment>
          <AnalysisCard
            minChartHeight='398px'
            idOfCard='model-info-text'
            topOfAppId=''
            key={'info-text-card'}
            chartType='infotext'
            //cardTitle={model.name}
            cardTitle='General Information'
            //footnoteText='Footnote Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.'
            //showDetailsOnHover={true}
            //heroTitle={model.name}
            infoText={description}
          />
        </React.Fragment>
      )
    } else {
      return null;
    }
  }

  render() {
    const {
      model,
    } = this.props;

    return (
      <div>
        <div className='row'>
          <div className='col-lg-12' style={{marginBottom:'30px'}}>
            <div>
              <a target='_new' href={model.portURL} style={{textDecoration:'none',fontSize:'22px',color:'#05A9B1',fontWeight:'900'}}>{model.name}</a>
            </div>
            <div style={{fontSize:'14px'}}>
              {'As of ' + formatDate(model.commonPeriodEnd) + ' with a Common Period from ' + formatDate(model.commonPeriodStart) + ' to ' + formatDate(model.commonPeriodEnd)}
            </div>
          </div>
        </div>
        <div className='row'>
          <div className='col-lg-4'>
            {this.renderInfoText()}
          </div>
          <div className='col-lg-4'>
            {this.renderKeyMetrics()}
          </div>
          <div className='col-lg-4'>
            {this.renderHoldingsChart()}
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {};
}

export default connect(
  mapStateToProps,
  {},
)(Model);
